import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DeliveryDriver from '../DeliveryDriver';
import DeliveryListHeader from './DeliveryListHeader';
import DeliveryItem from './DeliveryItem';
import NextDeliveryFooter from './NextDeliveryFooter';
import PackageScannerModal from './PackageScannerModal';
import BulkReceiveModal from './BulkReceiveModal';
import PackageHistoryModal from './PackageHistoryModal';
import DeliveryQuickActionsModal from './DeliveryQuickActionsModal';
import { useDriverOnWayNotification } from './useDriverOnWayNotification';

const STARTABLE_STATUSES = ['pending', 'postergado'];

function DeliveryList({ setActiveComponent, userId }) {
  const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [driver, setDriver] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeStarted, setRouteStarted] = useState(false);
  const [sequenceWarning, setSequenceWarning] = useState('');
  
  // Modal states
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryDelivery, setSelectedHistoryDelivery] = useState(null);
  const [selectedQuickActionsDelivery, setSelectedQuickActionsDelivery] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchDeliveryPoints();
      fetchDriverData();
    }
  }, [userId]);

  const fetchDriverData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/location/driver`, {
        params: { driver_id: userId },
      });
      setDriver(response.data);
    } catch (error) {
      console.error('Error fetching driver:', error);
    }
  };

  const fetchDeliveryPoints = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/delivery_points/driver/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDeliveries(response.data);
      
      // Check if route has been started
      const hasStartedRoute = response.data.some(
        d => !d.is_submitted && d.status && !STARTABLE_STATUSES.includes(d.status.toLowerCase())
      );
      setRouteStarted(hasStartedRoute);
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    }
  }, [userId]);

  // Use custom hook for driver on way notifications
  useDriverOnWayNotification(deliveries, setDeliveries);

  const handleDeliveriesRoutes = async () => {
    if (loadingRoutes || !driver) return;

    const startableDeliveries = deliveries.filter(
      d => STARTABLE_STATUSES.includes((d.status || '').toLowerCase()) && !d.is_submitted
    );
    
    if (startableDeliveries.length === 0) {
      alert('No hay entregas pendientes para procesar.');
      return;
    }

    setLoadingRoutes(true);

    try {
      await startSequenceRoute();
      await fetchDeliveryPoints();
      setRouteStarted(true);
      alert('¡Empezaste tu ruta, suerte!');
    } catch (error) {
      console.error('Error updating arrival times:', error);
      if (error.response?.data?.error || error.message) {
        alert(error.response?.data?.error || error.message);
        return;
      }
      alert('Algo falló, por favor intenta de nuevo.');
    } finally {
      setLoadingRoutes(false);
    }
  };

  const startSequenceRoute = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/driver/delivery-sequence-route/start`,
      { driver_id: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.data.message !== 'Sequence route started successfully') {
      throw new Error('No se pudo iniciar la ruta por secuencia');
    }

    return response.data;
  };

  /**
   * Handle package scanned - refresh delivery list
   */
  const handlePackageScanned = (data) => {
    // Refresh delivery list to update status icons
    fetchDeliveryPoints();
  };

  /**
   * Handle bulk receive - refresh delivery list
   */
  const handleBulkReceived = (data) => {
    // Refresh delivery list to update status icons
    fetchDeliveryPoints();
  };

  /**
   * Show package history modal
   */
  const handleShowHistory = (delivery) => {
    setSelectedHistoryDelivery(delivery);
    setShowHistoryModal(true);
  };

  // Separate deliveries into active and completed
  const activeDeliveries = deliveries
    .filter(d => !d.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  const completedDeliveries = deliveries
    .filter(d => d.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  // Find next delivery
  const nextDelivery = activeDeliveries[0];

  const showSequenceWarning = () => {
    setSequenceWarning(
      'Sigue la secuencia asignada. Si necesitas cambiar el orden, contacta a Fast Max para actualizar la secuencia.'
    );
  };

  useEffect(() => {
    if (!sequenceWarning) return undefined;

    const timeout = setTimeout(() => {
      setSequenceWarning('');
    }, 3500);

    return () => clearTimeout(timeout);
  }, [sequenceWarning]);

  // Check if can start route
  const nonSubmittedDeliveries = deliveries.filter(d => !d.is_submitted);
  const allStartable = nonSubmittedDeliveries.every(d =>
    STARTABLE_STATUSES.includes((d.status || '').toLowerCase())
  );
  const canStartRoute = allStartable && nonSubmittedDeliveries.length > 0 && !routeStarted;

  const handleBackFromDelivery = () => {
    setSelectedDeliveryPoint(null);
    fetchDeliveryPoints();
  };

  const handleDeliveryClick = (delivery) => {
    if (!nextDelivery || delivery.id === nextDelivery.id) {
      setSelectedDeliveryPoint(delivery);
      return;
    }

    setSelectedQuickActionsDelivery(delivery);
    showSequenceWarning();
  };

  if (selectedDeliveryPoint) {
    return (
      <DeliveryDriver
        delivery={selectedDeliveryPoint}
        onBack={handleBackFromDelivery}
        setActiveComponent={setActiveComponent}
        driverId={userId}
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <DeliveryListHeader
        setActiveComponent={setActiveComponent}
        onStartRoute={handleDeliveriesRoutes}
        canStartRoute={canStartRoute}
        loadingRoutes={loadingRoutes}
        routeStarted={routeStarted}
        onOpenScanner={() => setShowScannerModal(true)}
        onOpenBulkReceive={() => setShowBulkReceiveModal(true)}
      />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-24">
        {sequenceWarning && (
          <div className="sticky top-0 z-20 px-4 pt-3">
            <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 shadow-sm font-montserrat">
              {sequenceWarning}
            </div>
          </div>
        )}

        <h1 className="text-3xl font-montserrat font-bold text-brand-primary px-5 py-4">
          Entregas
        </h1>

        {/* Active Deliveries */}
        {activeDeliveries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Activas
            </h2>
            <div className="bg-white">
              {activeDeliveries.map((delivery, index) => (
                <DeliveryItem
                  key={delivery.id}
                  delivery={delivery}
                  onClick={() => handleDeliveryClick(delivery)}
                  isLast={index === activeDeliveries.length - 1}
                  isLocked={nextDelivery?.id !== delivery.id}
                  onShowHistory={handleShowHistory}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Deliveries */}
        {completedDeliveries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Completadas
            </h2>
            <div className="bg-white opacity-60">
              {completedDeliveries.map((delivery, index) => (
                <DeliveryItem
                  key={delivery.id}
                  delivery={delivery}
                  onClick={() => {}}
                  isLast={index === completedDeliveries.length - 1}
                  disabled={true}
                  onShowHistory={handleShowHistory}
                />
              ))}
            </div>
          </div>
        )}

        {deliveries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay entregas asignadas
          </div>
        )}
      </div>

      {/* Fixed Footer - Next Delivery Quick Access */}
      {nextDelivery && (
        <NextDeliveryFooter
          delivery={nextDelivery}
          onClick={() => setSelectedDeliveryPoint(nextDelivery)}
        />
      )}

      {/* Modals */}
      <PackageScannerModal
        isOpen={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        driverId={userId}
        onPackageScanned={handlePackageScanned}
      />

      <BulkReceiveModal
        isOpen={showBulkReceiveModal}
        onClose={() => setShowBulkReceiveModal(false)}
        driverId={userId}
        deliveries={deliveries}
        onBulkReceived={handleBulkReceived}
      />

      <PackageHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        deliveryPointId={selectedHistoryDelivery?.id}
        trackingCode={selectedHistoryDelivery?.tracking_code}
      />

      <DeliveryQuickActionsModal
        key={selectedQuickActionsDelivery?.id || 'closed'}
        open={!!selectedQuickActionsDelivery}
        onClose={() => setSelectedQuickActionsDelivery(null)}
        delivery={selectedQuickActionsDelivery}
        driverId={userId}
      />
    </div>
  );
}

export default DeliveryList;
